// src/components/PortalAluno/DashboardAluno.tsx
// UPDATED: 2026-03-27 — conectado ao backend, visual melhorado

import React, { useMemo } from 'react';
import {
  Award, BookOpen, CreditCard, Clock, User,
  ChevronRight, Bell, TrendingUp, ArrowRight,
  Loader2, AlertTriangle,
} from 'lucide-react';
import { useStudentProfile } from '../../hooks/useStudentProfile';

const DashboardAluno: React.FC = () => {
  const { student, loading, error } = useStudentProfile();

  // Diploma status
  const diplomaStatus = useMemo(() => {
    if (!student?.diplomaRequests?.length) return null;
    return student.diplomaRequests[student.diplomaRequests.length - 1];
  }, [student]);

  const cards = useMemo(() => [
    {
      label: 'Certificados',
      value: student?.certificateCount ?? 0,
      subtitle: 'emitidos',
      icon: Award,
      dot: '#d4af37',
      bg: 'rgba(212,175,55,0.12)',
    },
    {
      label: 'Curso',
      value: student?.courseName ? '1' : '0',
      subtitle: 'em andamento',
      icon: BookOpen,
      dot: '#3b82f6',
      bg: 'rgba(59,130,246,0.10)',
    },
    {
      label: 'Carga',
      value: student?.courseWorkload ? `${student.courseWorkload}h` : '—',
      subtitle: 'horas do curso',
      icon: Clock,
      dot: '#22c55e',
      bg: 'rgba(34,197,94,0.10)',
    },
    {
      label: 'Carteira',
      value: student?.carteiraGerada ? 'Ativa' : 'Aluno',
      subtitle: 'profissional',
      icon: CreditCard,
      dot: '#a855f7',
      bg: 'rgba(168,85,247,0.10)',
    },
  ], [student]);

  const actions = [
    { label: 'Ver Carteira Digital', description: 'Acesse seu QR Code profissional',  icon: CreditCard, dot: '#d4af37' },
    { label: 'Meus Certificados',    description: 'Baixe e valide seus certificados', icon: Award,      dot: '#3b82f6' },
    { label: 'Minha Carreira',       description: 'Acompanhe sua evolução',           icon: TrendingUp, dot: '#a855f7' },
    { label: 'Meus Dados',           description: 'Atualize seu cadastro e perfil',   icon: User,       dot: '#22c55e' },
  ];

  const steps = [
    { step: '1', description: 'Complete seu cadastro e dados pessoais',   linkText: 'Completar dados'   },
    { step: '2', description: 'Acesse e baixe seus certificados',          linkText: 'Ver certificados'  },
    { step: '3', description: 'Ative sua carteira profissional digital',   linkText: 'Ativar carteira'   },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 text-amber-500 animate-spin"/>
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-3 py-12 text-center">
      <AlertTriangle className="w-6 h-6 text-red-400 mx-auto"/>
      <p className="text-sm text-red-500">{error}</p>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full"/>
            <img src="/icons/painel.png" alt="Dashboard"
              className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
              Meu Painel
            </h1>
            <p className="text-xs text-gray-400 mt-1">
              {student ? `Olá, ${student.fullName.split(' ')[0]}! Bem-vindo ao seu portal.` : 'Acompanhe seus cursos e certificados'}
            </p>
          </div>
        </div>

        {/* Status da matrícula */}
        {student && (
          <div className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
            <Bell className="w-4 h-4 text-amber-500 flex-shrink-0"/>
            <div>
              <p className="text-xs font-medium text-amber-700 leading-none">
                {student.status === 'active' ? 'Matrícula Ativa' : student.status === 'graduated' ? 'Formado' : 'Matrícula'}
              </p>
              <p className="text-[10px] text-amber-500 mt-0.5">
                Desde {new Date(student.enrollmentDate).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── CURSO ATUAL ─────────────────────────────────────────────── */}
      {student?.courseName && (
        <div className="bg-gradient-to-r from-[#0f1b2e] to-[#1a2d42] rounded-2xl p-5 flex items-center gap-4 shadow-[0_4px_20px_rgba(0,0,0,0.15)]">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(212,175,55,0.20)' }}>
            <BookOpen className="w-6 h-6 text-amber-400"/>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-amber-400/70 uppercase tracking-widest font-medium mb-0.5">Curso atual</p>
            <p className="text-sm font-semibold text-white truncate">{student.courseName}</p>
            <p className="text-[11px] text-white/40 mt-0.5">
              {student.courseWorkload ? `${student.courseWorkload}h` : ''}
              {student.affiliate?.schoolName ? ` · ${student.affiliate.schoolName}` : ''}
            </p>
          </div>
          {diplomaStatus && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg flex-shrink-0 ${
              diplomaStatus.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
              diplomaStatus.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
              'bg-amber-500/20 text-amber-400'
            }`}>
              {diplomaStatus.status === 'approved' ? 'Diploma aprovado' :
               diplomaStatus.status === 'rejected' ? 'Diploma recusado' : 'Diploma em análise'}
            </span>
          )}
        </div>
      )}

      {/* ── STAT CARDS ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map(({ label, value, subtitle, icon: Icon, dot, bg }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100
            shadow-[0_1px_6px_rgba(0,0,0,0.04)] px-4 py-4 flex flex-col gap-3
            hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: bg }}>
              <Icon className="w-[18px] h-[18px]" style={{ color: dot }}/>
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-900 leading-none">{value}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{label}</p>
              <p className="text-[10px] text-gray-300 mt-0.5">{subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── AÇÕES RÁPIDAS ──────────────────────────────────────────── */}
      <div>
        <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Ações Rápidas
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {actions.map(({ label, description, icon: Icon, dot }) => (
            <button key={label}
              className="flex items-center gap-4 px-4 py-4 bg-white rounded-2xl
                border border-gray-100 shadow-[0_1px_6px_rgba(0,0,0,0.04)]
                hover:border-gray-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.07)]
                transition-all group text-left">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: dot + '18' }}>
                <Icon className="w-5 h-5" style={{ color: dot }}/>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{description}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-400
                group-hover:translate-x-0.5 transition-all flex-shrink-0"/>
            </button>
          ))}
        </div>
      </div>

      {/* ── PRÓXIMOS PASSOS ────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100
        shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-400"/>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
            Próximos Passos
          </p>
        </div>
        <div className="divide-y divide-gray-50">
          {steps.map(({ step, description, linkText }) => (
            <div key={step}
              className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                text-xs font-semibold text-[#1a1a1c]"
                style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                {step}
              </div>
              <p className="flex-1 text-sm text-gray-600">{description}</p>
              <button className="flex items-center gap-1 text-xs font-medium text-amber-600
                hover:text-amber-700 transition-colors flex-shrink-0">
                {linkText} <ChevronRight className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardAluno;